import api from '@/apis'
import { createLatestOnly } from '@/utils/latestOnly'
import { createUiSession } from '@/utils/uiSession'

// Two questions, two tokens — see editRouteDialog for the full note.
//   create_request  REQUEST OWNERSHIP, claimed by the action; gates the snackbar.
//   dialog_session  UI SESSION, advanced by a transition; gates the close.
const create_request = createLatestOnly()
const dialog_session = createUiSession()

// The one transition path: every mutation that changes which dialog is on screen goes through it.
function showDialog(state, visible) {
  state.visibility = visible
  dialog_session.transition()
}

export default {
  state: {
    visibility: false,
    from_level: null,
    from_level_data: null
  },
  mutations: {
    // The open path MenuSetting.vue actually uses.
    INIT_NEW_ROUTE_DIALOG(state, payload) {
      state.from_level = payload.from_level
      state.from_level_data = payload.data
      showDialog(state, true)
    },
    SET_NEW_ROUTE_DIALOG_VISIBILITY(state, payload) {
      showDialog(state, payload)
    }
  },
  actions: {
    async createNewRoute({ commit, rootState, dispatch }, payload) {
      // MUTATION, not a read. The server has already done this; being superseded cannot undo it,
      // so a SUCCESS must always reconcile local state. Only the DISPLAY — dialog visibility and
      // snackbar text — is ownership-gated. (Round 7 applied the read rule here and the UI kept
      // showing rows the server had deleted.) See utils/latestOnly.js.
      const isLatest = create_request()
      // Captured, not claimed: belonging to a dialog is not exclusive.
      const sameDialog = dialog_session.current()
      const say = snackbar => {
        if (isLatest()) commit('SET_SNACKBAR', snackbar)
      }
      // Captured BEFORE the await. Moving the reconciliation out of the ownership gate means it
      // now runs for a superseded request too — and by then the operator may have opened the
      // dialog for a DIFFERENT route, so reading these afterwards would refresh the wrong level.
      // The request must reconcile the route IT changed.
      const from_level = rootState.newRouteDialog.from_level
      // Once the server has ANSWERED, a local failure is not a server failure. `commit` runs Vuex
      // subscribers and a throwing one propagates, so everything after the response — reconciling,
      // closing, even the snackbar itself — sits inside this try. Reporting "Server Error" there
      // tells the operator their change failed when the server accepted it, which is the most
      // damaging thing this action can say. The request's own failure still reports normally.
      let answered = false
      try {
        say({ status: true, color: 'loading', text: 'Creating route...', loading: true })
        const response = await api.createRoute(payload)
        answered = true
        if (response.data.success) {
          // RECONCILE — but only what is ON SCREEN. Re-fetching is itself a repaint of the one
          // visible panel, so an older mutation whose parent is no longer displayed must NOT pull
          // the operator's panel back to it. If its change is not on screen, nothing on screen is
          // stale, and the next visit to that parent reads it fresh.
          // `parent_id` comes from the request payload, so it is already the right one.
          if (from_level === 2) {
            if (rootState.menuSetting.level_2_menu_data.route_id === payload.params.parent_id) dispatch('fetchLevel2Route', payload.params.parent_id)
          } else if (rootState.menuSetting.level_3_menu_data.route_id === payload.params.parent_id) {
            dispatch('fetchLevel3Route', payload.params.parent_id)
          }
          say({ status: true, color: 'success', text: 'Success', icon: 'check', timeout: '2000' })
          // DISPLAY — gated on both tokens, and after the verdict; see editRouteDialog::updateRoute.
          if (isLatest() && sameDialog()) commit('SET_NEW_ROUTE_DIALOG_VISIBILITY', false)
        } else {
          say({ status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '2000' })
        }
      } catch (error) {
        if (!answered) say({ status: true, color: 'error', text: 'Server Error', icon: 'close', timeout: '2000' })
      }
    }
  }
}
